import { useEffect, useState } from 'react';

const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;

// How long after the first interaction the deferred UI/tags may mount. The flip used to run
// synchronously inside the interaction's own pointerdown, so the visitor's first tap paid
// for an App-wide re-render on top of whatever it was tapping —
// measured as a ~100 ms "first-tap tax" on the hamburger. Deferring the flip to idle time
// keeps it out of that tap's INP window; the fallback timer covers hosts without rIC.
const IDLE_TIMEOUT_MS = 1500;

const scheduleIdle = (callback: () => void) => {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(callback, 300);
  return () => window.clearTimeout(id);
};

/**
 * Defers mounting expensive, non-critical UI (e.g. the AI chat FAB) until the user's
 * first interaction, with a timeout fallback so it still appears on idle visits.
 * Keeps heavy bundle evaluation (CopilotKit ≈1.1 s of main-thread time) out of the
 * page-load critical path measured by LCP/TBT.
 */
export const useDeferredMount = (fallbackDelayMs = 10000) => {
  const [shouldMount, setShouldMount] = useState(false);

  useEffect(() => {
    if (shouldMount) {
      return undefined;
    }
    let cancelIdle: (() => void) | null = null;
    const trigger = () => {
      if (cancelIdle) return;
      // Never flip state inside the interaction that woke us up — let its paint go first.
      cancelIdle = scheduleIdle(() => setShouldMount(true));
    };
    INTERACTION_EVENTS.forEach((event) => window.addEventListener(event, trigger, { once: true, passive: true }));
    const timer = window.setTimeout(trigger, fallbackDelayMs);
    return () => {
      INTERACTION_EVENTS.forEach((event) => window.removeEventListener(event, trigger));
      window.clearTimeout(timer);
      cancelIdle?.();
    };
  }, [shouldMount, fallbackDelayMs]);

  return shouldMount;
};

/**
 * `true` a while after `active` became true — for staggering things that would otherwise
 * mount (and run their long tasks) in the same burst, e.g. the analytics tags after consent.
 */
export const useDelayedFlag = (active: boolean, delayMs: number) => {
  const [flag, setFlag] = useState(false);

  useEffect(() => {
    if (!active || flag) {
      return undefined;
    }
    const timer = window.setTimeout(() => setFlag(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [active, flag, delayMs]);

  return flag;
};
